import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import {
    FiArrowRight,
    FiArrowLeft,
    FiCheck,
    FiGlobe,
    FiLink,
    FiZap,
    FiSmartphone,
    FiServer,
    FiCloud,
    FiShield,
    FiAlertTriangle,
    FiInfo,
    FiX,
    FiEdit2,
    FiUpload,
    FiDownload,
    FiFileText,
    FiFile,
    FiPlus,
    FiTrash2,
    FiRefreshCw,
    FiCheckCircle
} from "react-icons/fi";
import axios from "../../../utils/api/axios";
import { toast } from "react-hot-toast";

interface CreateTargetWizardProps {
    assetId: string;
    assetName: string;
    assetCategory: string;
    assetType: string[];
    mobilePlatforms?: string[];
    onClose: () => void;
    onSuccess: () => void;
}

const TARGET_TEMPLATES = [
    {
        id: "web-url",
        name: "Web URL Target",
        icon: <FiGlobe className="w-6 h-6" />,
        description: "Public or internal web application URL",
        color: "blue",
        fields: ["baseUrl", "internetExposure"],
        recommendedFor: [
            { category: "Digital Applications", type: "Web Application" },
            { category: "Infrastructure", type: "External Facing web applications" },
            { category: "Infrastructure", type: "External infrastructure" },
            { category: "Infrastructure", type: "Internal infrastructure" },
            { category: "Identity & Data", type: "Identity Provider" },
            { category: "Code & Supply Chain", type: "Source Code Repository" },
            { category: "Code & Supply Chain", type: "CI/CD Pipeline" }
        ]
    },
    {
        id: "admin-web-url",
        name: "Admin/Restricted Web URL",
        icon: <FiShield className="w-6 h-6" />,
        description: "Admin interface or restricted access web URL",
        color: "purple",
        fields: ["baseUrl", "internetExposure"],
        recommendedFor: [
            { category: "Digital Applications", type: "Web Application" },
            { category: "Infrastructure", type: "External Facing web applications" },
            { category: "Code & Supply Chain", type: "CI/CD Pipeline" }
        ]
    },
    {
        id: "api-base",
        name: "API Base URI Target",
        icon: <FiLink className="w-6 h-6" />,
        description: "REST API or GraphQL endpoint base URI",
        color: "indigo",
        fields: ["baseUri", "authenticationRequired"],
        recommendedFor: [
            { category: "Digital Applications", type: "API Service" },
            { category: "Digital Applications", type: "Web Application" },
            { category: "Digital Applications", type: "Mobile Application" },
            { category: "Integrations & Devices", type: "External Integration" }
        ]
    },
    {
        id: "webhook-url",
        name: "Webhook/Callback URL",
        icon: <FiLink className="w-6 h-6" />,
        description: "Webhook endpoint or callback URL",
        color: "cyan",
        fields: ["webhookUrl"],
        recommendedFor: [
            { category: "Digital Applications", type: "API Service" },
            { category: "Integrations & Devices", type: "Webhook Endpoint" }
        ]
    },
    // New Mobile Templates
    {
        id: "android-app",
        name: "Android App",
        icon: <FiSmartphone className="w-6 h-6" />,
        description: "Android App (APK/AAB or Play Store)",
        color: "green",
        fields: ["playStoreUrl", "fileUpload"],
        recommendedFor: [
            { category: "Digital Applications", type: "Mobile Application", platform: "Android" }
        ]
    },
    {
        id: "ios-app",
        name: "iOS App",
        icon: <FiSmartphone className="w-6 h-6" />,
        description: "iOS App (IPA, TestFlight, or App Store)",
        color: "gray",
        fields: ["appStoreUrl", "fileUpload"],
        recommendedFor: [
            { category: "Digital Applications", type: "Mobile Application", platform: "iOS" }
        ]
    },
    {
        id: "mobile-app", // Fallback generic
        name: "Mobile App Target",
        icon: <FiSmartphone className="w-6 h-6" />,
        description: "Mobile application package or store link",
        color: "purple",
        fields: ["appIdentifier"],
        recommendedFor: [
            { category: "Digital Applications", type: "Mobile Application" }
        ]
    },
    {
        id: "ip-host",
        name: "External IP / Host Target",
        icon: <FiServer className="w-6 h-6" />,
        description: "Individual server IP address or hostname",
        color: "slate",
        fields: ["targetValue"],
        recommendedFor: [
            { category: "Infrastructure", type: "Server / Host" },
            { category: "Infrastructure", type: "External infrastructure" },
            { category: "Infrastructure", type: "Internal infrastructure" },
            { category: "Integrations & Devices", type: "Device / IoT Component" }
        ]
    },
    {
        id: "cidr-network",
        name: "CIDR / Network Segment Target",
        icon: <FiServer className="w-6 h-6" />,
        description: "IP range or complete network segment",
        color: "slate",
        fields: ["cidr"],
        recommendedFor: [
            { category: "Infrastructure", type: "Network Segment" },
            { category: "Infrastructure", type: "External infrastructure" },
            { category: "Infrastructure", type: "Internal infrastructure" }
        ]
    },
    {
        id: "cloud-resource",
        name: "Cloud Account/Project Target",
        icon: <FiCloud className="w-6 h-6" />,
        description: "AWS account, Azure subscription, or GCP project",
        color: "cyan",
        fields: ["cloudProvider", "accountId"],
        recommendedFor: [
            { category: "Infrastructure", type: "Cloud instance" }
        ]
    },
    {
        id: "custom",
        name: "Custom Target",
        icon: <FiZap className="w-6 h-6" />,
        description: "Define a custom target testing boundary",
        color: "amber",
        fields: ["targetValue", "customType"],
        recommendedFor: [] // Always available as escape hatch
    }
];

const CreateTargetWizard: React.FC<CreateTargetWizardProps> = ({
    assetId,
    assetName,
    assetCategory,
    assetType,
    mobilePlatforms,
    onClose,
    onSuccess
}) => {
    const [step, setStep] = useState(0); // 0: Info, 1: Mode Selection, 2: Action (Table or Upload)
    const [importMode, setImportMode] = useState<'table' | 'bulk'>('table');
    const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
    const [formData, setFormData] = useState<any>({
        target_name: "",
        target_type: "",
        environment: "Production",
        target_value: "",
        metadata: {},
        ownership_confirmed: false,
        environment_confirmed: false,
        approved_by: "",
        notes: "",
    });
    const [loading, setLoading] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [uploading, setUploading] = useState(false); // For file upload

    // Bulk import state
    const [importFormat, setImportFormat] = useState<'csv' | 'json'>('csv');
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importData, setImportData] = useState<any[]>([]);
    const [importErrors, setImportErrors] = useState<any[]>([]);
    const [importProgress, setImportProgress] = useState(0);
    const [bulkProcessing, setBulkProcessing] = useState(false);

    // Multiple Target State
    const [multipleTargets, setMultipleTargets] = useState<any[]>([]);
    const [globalSettings, setGlobalSettings] = useState({
        environment: "Production",
        internetExposure: "Internal",
        ownership_confirmed: false,
        environment_confirmed: false,
    });
    const [showImportGuide, setShowImportGuide] = useState(false);

    // Get template-specific field configuration
    const getFieldConfig = (templateId: string) => {
        const configs: Record<string, { label: string; placeholder: string; pattern?: RegExp; hint?: string }> = {
            "web-url": {
                label: "Web Application URL",
                placeholder: "https://app.example.com",
                pattern: /^https?:\/\/.+/,
                hint: "Must be a valid URL starting with http:// or https://"
            },
            "admin-web-url": {
                label: "Admin/Restricted URL",
                placeholder: "https://admin.example.com",
                pattern: /^https?:\/\/.+/,
                hint: "Must be a valid URL starting with http:// or https://"
            },
            "api-base": {
                label: "API Base URI",
                placeholder: "https://api.example.com/v1",
                pattern: /^https?:\/\/.+/,
                hint: "Must be a valid base URI starting with http:// or https://"
            },
            "webhook-url": {
                label: "Webhook/Callback URL",
                placeholder: "https://webhook.example.com/callback",
                pattern: /^https?:\/\/.+/,
                hint: "Must be a valid URL starting with http:// or https://"
            },
            "android-app": {
                label: "Play Store URL or Package ID",
                placeholder: "https://play.google.com/store/apps/details?id=com.example or com.example",
                hint: "Provide Play Store link or Package Name"
            },
            "ios-app": {
                label: "App Store URL or Bundle ID",
                placeholder: "https://apps.apple.com/app/id123456 or com.example.app",
                hint: "Provide App Store link or Bundle ID"
            },
            "mobile-app": {
                label: "App Bundle/Package ID",
                placeholder: "com.company.appname or com.company.App",
                pattern: /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/,
                hint: "Must be a valid bundle identifier (e.g., com.company.app)"
            },
            "ip-host": {
                label: "IP Address or Hostname",
                placeholder: "192.168.1.1 or server.example.com",
                pattern: /^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}|[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*)$/,
                hint: "Must be a valid IP address or hostname"
            },
            "cidr-network": {
                label: "CIDR Network Range",
                placeholder: "10.0.0.0/24 or 192.168.0.0/16",
                pattern: /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/,
                hint: "Must be a valid CIDR notation (e.g., 10.0.0.0/24)"
            },
            "cloud-resource": {
                label: "Cloud Account/Project ID",
                placeholder: "arn:aws:iam::123456789012 or project-id",
                hint: "Enter your cloud account identifier, project ID, or ARN"
            },
            "custom": {
                label: "Custom Target Value",
                placeholder: "Enter your custom target identifier",
                hint: "Define your custom target value"
            }
        };
        return configs[templateId] || configs["custom"];
    };

    // Validate target value based on template
    const validateTargetValue = (value: string, templateId: string): string => {
        // If it's a mobile app and a file has been uploaded, value is optional
        const isMobile = ["android-app", "ios-app"].includes(templateId);
        if (isMobile && formData.metadata.binary_url) {
            return "";
        }

        if (!value || !value.trim()) {
            return "This field is required";
        }

        const config = getFieldConfig(templateId);
        if (config.pattern && !config.pattern.test(value)) {
            return config.hint || "Invalid format";
        }

        return ""; // Valid
    };

    // ==================== BULK IMPORT FUNCTIONS ====================

    // Generate CSV template
    const generateCSVTemplate = () => {
        const headers = [
            'target_name',
            'target_type',
            'target_value',
            'environment',
            'exposure',
            'notes'
        ];

        const exampleRows = [
            [
                'Production API',
                'API Base URI Target',
                'https://api.example.com/v1',
                'Production',
                'External',
                'Main production API'
            ],
            [
                'Staging Web App',
                'Web URL Target',
                'https://staging.example.com',
                'Staging',
                'Internal',
                'Staging environment'
            ]
        ];

        const csvContent = [
            headers.join(','),
            ...exampleRows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `bulk_targets_template_${assetName.replace(/\s+/g, '_')}.csv`;
        link.click();
        toast.success('CSV template downloaded!');
    };

    // Generate JSON template
    const generateJSONTemplate = () => {
        const template = [
            {
                target_name: 'Production API',
                target_type: 'API Base URI Target',
                target_value: 'https://api.example.com/v1',
                environment: 'Production',
                exposure: 'External',
                notes: 'Main production API'
            },
            {
                target_name: 'Staging Web App',
                target_type: 'Web URL Target',
                target_value: 'https://staging.example.com',
                environment: 'Staging',
                exposure: 'Internal',
                notes: 'Staging environment'
            }
        ];

        const jsonContent = JSON.stringify(template, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `bulk_targets_template_${assetName.replace(/\s+/g, '_')}.json`;
        link.click();
        toast.success('JSON template downloaded!');
    };

    // Parse CSV file
    const parseCSVFile = async (file: File) => {
        return new Promise<any[]>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target?.result as string;
                    const lines = text.split('\n').filter(line => line.trim());

                    if (lines.length < 2) {
                        reject(new Error('CSV file must contain headers and at least one data row'));
                        return;
                    }

                    // Parse headers
                    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));

                    // Parse data rows
                    const data = [];
                    for (let i = 1; i < lines.length; i++) {
                        const values: string[] = [];
                        let currentValue = '';
                        let insideQuotes = false;

                        for (let j = 0; j < lines[i].length; j++) {
                            const char = lines[i][j];
                            if (char === '"') {
                                insideQuotes = !insideQuotes;
                            } else if (char === ',' && !insideQuotes) {
                                values.push(currentValue.trim());
                                currentValue = '';
                            } else {
                                currentValue += char;
                            }
                        }
                        values.push(currentValue.trim());

                        const row: any = {};
                        headers.forEach((header, idx) => {
                            let value = values[idx] || '';
                            row[header] = value;
                        });
                        data.push(row);
                    }

                    resolve(data);
                } catch (error: any) {
                    reject(new Error(`CSV parsing error: ${error.message} `));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    };

    // Parse JSON file
    const parseJSONFile = async (file: File) => {
        return new Promise<any[]>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target?.result as string;
                    const data = JSON.parse(text);

                    if (!Array.isArray(data)) {
                        reject(new Error('JSON file must contain an array of targets'));
                        return;
                    }

                    if (data.length === 0) {
                        reject(new Error('JSON file must contain at least one target'));
                        return;
                    }

                    resolve(data);
                } catch (error: any) {
                    reject(new Error(`JSON parsing error: ${error.message} `));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    };

    // Validate import row
    const validateImportRow = (row: any, index: number) => {
        const errors: string[] = [];

        // Required fields
        if (!row.target_name || !row.target_name.trim()) {
            errors.push('target_name is required');
        }
        if (!row.target_type || !row.target_type.trim()) {
            errors.push('target_type is required');
        }
        if (!row.target_value || !row.target_value.trim()) {
            errors.push('target_value is required');
        }
        if (!row.environment || !row.environment.trim()) {
            errors.push('environment is required');
        }

        // Validate environment (Case-Insensitive)
        const validEnvironments = ['Production', 'Staging', 'UAT', 'Development'];
        const envVal = (row.environment || "").trim();
        const isEnvValid = validEnvironments.some(e => e.toLowerCase() === envVal.toLowerCase());
        if (envVal && !isEnvValid) {
            errors.push(`environment must be one of: ${validEnvironments.join(', ')} `);
        }

        // Validate exposure (Case-Insensitive)
        const exposureVal = (row.exposure || "").trim();
        const isExposureValid = ['Internal', 'External'].some(e => e.toLowerCase() === exposureVal.toLowerCase());
        if (!isExposureValid) {
            errors.push('exposure must be either Internal or External');
        }

        // Validate target_type exists (Case-Insensitive)
        const allowedTypeNames = filteredTemplates.map(t => t.name);
        const typeVal = (row.target_type || "").trim();
        const isTypeValid = allowedTypeNames.some(t => t.toLowerCase() === typeVal.toLowerCase());
        if (typeVal && !isTypeValid) {
            errors.push(`target_type '${typeVal}' is not relevant for this asset. Allowed: ${allowedTypeNames.join(', ')}`);
        }

        return errors;
    };

    // Handle bulk file upload
    const handleBulkFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImportFile(file);
        setImportData([]);
        setImportErrors([]);

        try {
            let data: any[];
            if (importFormat === 'csv') {
                data = await parseCSVFile(file);
            } else {
                data = await parseJSONFile(file);
            }

            // Validate all rows
            const errors = data.map((row, idx) => ({
                index: idx,
                errors: validateImportRow(row, idx)
            }));

            setImportData(data);
            setImportErrors(errors);

            const validCount = errors.filter(e => e.errors.length === 0).length;
            const invalidCount = errors.length - validCount;

            if (invalidCount === 0) {
                toast.success(`${validCount} targets ready to import !`);
            } else {
                toast.error(`Found ${invalidCount} invalid rows.Please review.`);
            }
        } catch (error: any) {
            toast.error(error.message);
            setImportFile(null);
        }
    };



    // Filter templates based on asset category and type
    const filteredTemplates = React.useMemo(() => {
        let parsedAssetTypes: string[] = [];
        if (typeof assetType === 'string') {
            try {
                const parsed = JSON.parse(assetType);
                parsedAssetTypes = Array.isArray(parsed) ? parsed : [assetType];
            } catch (e) {
                parsedAssetTypes = [assetType];
            }
        } else if (Array.isArray(assetType)) {
            parsedAssetTypes = assetType;
        } else {
            parsedAssetTypes = [String(assetType)];
        }

        const customTemplate = TARGET_TEMPLATES.find(t => t.id === "custom");

        const matchingTemplates = TARGET_TEMPLATES.filter(template => {
            if (template.id === "custom") return false;
            // Explicit platform filtering for mobile apps
            if (template.id === "android-app") {
                return mobilePlatforms?.includes("Android");
            }
            if (template.id === "ios-app") {
                return mobilePlatforms?.includes("iOS");
            }
            if (template.id === "mobile-app") {
                // Only show generic mobile app if NO specific platforms selected or if implied
                // If specific platforms are selected, we prefer the specific templates
                if (mobilePlatforms && mobilePlatforms.length > 0) return false;
            }

            if (!template.recommendedFor || template.recommendedFor.length === 0) {
                return false;
            }
            return template.recommendedFor.some(rec => {
                const categoryMatches = rec.category === assetCategory;
                const typeMatches = parsedAssetTypes.includes(rec.type);
                return categoryMatches && typeMatches;
            });
        });

        return customTemplate
            ? [...matchingTemplates, customTemplate]
            : matchingTemplates;
    }, [assetCategory, assetType, mobilePlatforms]);

    const nextStep = () => {
        if (step === 0 && uploading) {
            toast.error("Please wait for the file to finish uploading");
            return;
        }
        if (step === 2) {
            if (importMode === 'table') {
                if (multipleTargets.some(t => t.uploading)) {
                    toast.error("Please wait for all files to finish uploading");
                    return;
                }
                const invalidRows = multipleTargets.filter(t => !t.target_name || (!t.target_value && !t.file_url));
                if (invalidRows.length > 0) {
                    toast.error("Please fill in target name and (URL or File) for all rows");
                    return;
                }
                if (multipleTargets.length === 0) {
                    toast.error("Please add at least one target");
                    return;
                }
            } else if (importMode === 'bulk') {
                if (importData.length === 0 || importErrors.some(e => e.errors.length > 0)) {
                    toast.error("Please upload a valid file first");
                    return;
                }
            }
        }
        setStep(s => s + 1);
    };
    const prevStep = () => setStep(s => s - 1);

    const handleModeSelect = (mode: 'table' | 'bulk') => {
        setImportMode(mode);
        if (mode === 'table') {
            // Initialize with one empty row using the first filtered template
            const defaultTemplate = filteredTemplates[0] || TARGET_TEMPLATES[0];
            if (multipleTargets.length === 0) {
                setMultipleTargets([{
                    id: Date.now(),
                    target_type: defaultTemplate.name,
                    template_id: defaultTemplate.id,
                    target_name: "",
                    target_value: "",
                    environment: globalSettings.environment,
                    internetExposure: globalSettings.internetExposure,
                    ownership_confirmed: false,
                    environment_confirmed: false,
                    notes: "",
                    auth_required: false
                }]);
            }
        }
        setStep(2); // Move to next step (Table or Upload)
    };


    const handleInputChange = (field: string, value: any) => {
        // Auto-trim target_value to prevent validation errors from accidental spaces
        if (field === "target_value" && typeof value === "string") {
            value = value.trim();
        }

        if (field.startsWith("meta.")) {
            const metaField = field.split(".")[1];
            setFormData({
                ...formData,
                metadata: { ...formData.metadata, [metaField]: value }
            });
        } else {
            setFormData({ ...formData, [field]: value });

            if (field === "target_value" && selectedTemplate) {
                const error = validateTargetValue(value, selectedTemplate.id);
                setValidationError(error);
            }
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const isLt100M = file.size / 1024 / 1024 < 250;
        if (!isLt100M) {
            toast.error("File must be smaller than 250MB!");
            return;
        }

        setUploading(true);
        const uploadFormData = new FormData();
        uploadFormData.append("file", file);

        try {
            const response = await axios.post("/assets/upload", uploadFormData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            setFormData({
                ...formData,
                metadata: {
                    ...formData.metadata,
                    binary_url: response.data.url,
                    binary_name: file.name,
                    binary_type: file.type
                }
            });
            toast.success("Binary uploaded successfully");
        } catch (err) {
            console.error("Upload error:", err);
            toast.error("Failed to upload binary");
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async () => {
        if (uploading) {
            toast.error("Please wait for the file to finish uploading");
            return;
        }
        if (!formData.ownership_confirmed || !formData.environment_confirmed) {
            toast.error("Please confirm authorization and safety checks.");
            return;
        }

        // Final validation
        if (!formData.target_value && !formData.metadata.binary_url) {
            setValidationError("This field is required");
            return;
        }

        setLoading(true);
        try {
            // Provide fallback target_value if empty but binary exists
            const submissionData = {
                ...formData,
                target_value: formData.target_value || formData.metadata.binary_name || "Uploaded Binary"
            };
            await axios.post(`/assets/${assetId}/targets`, submissionData);
            toast.success("Target created successfully!");
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("Error creating target:", error);
            toast.error(error.response?.data?.message || "Failed to create target.");
        } finally {
            setLoading(false);
        }
    };

    const authRef = useRef<HTMLDivElement>(null);
    const [highlightAuth, setHighlightAuth] = useState(false);

    const handleMultipleSubmit = async () => {
        if (multipleTargets.some(t => t.uploading)) {
            toast.error("Please wait for all files to finish uploading");
            return;
        }
        if (!globalSettings.ownership_confirmed || !globalSettings.environment_confirmed) {
            toast.error("Please confirm scope authorization and operational safety.");
            return;
        }

        // Filter out completely empty rows
        const targetsToSubmit = multipleTargets.filter(t => t.target_name || t.target_value || t.file_url);
        
        if (targetsToSubmit.length === 0) {
            toast.error("Please add at least one target.");
            return;
        }

        // Validate that each target has a name and either a value or a file
        const invalidRows = targetsToSubmit.filter(t => !t.target_name || (!t.target_value && !t.file_url));
        if (invalidRows.length > 0) {
            toast.error("Please provide name and (URL or File) for all targets.");
            return;
        }

        const formattedTargets = targetsToSubmit.map(t => ({
            target_name: t.target_name,
            target_type: t.target_type,
            target_value: t.target_value || t.file_name || "Uploaded Binary",
            environment: t.environment,
            ownership_confirmed: globalSettings.ownership_confirmed,
            environment_confirmed: globalSettings.environment_confirmed,
            notes: t.notes || "Batch created via Target Wizard",
            metadata: {
                authenticationRequired: t.auth_required || false,
                internetExposure: t.internetExposure,
                ...(t.file_url && {
                    binary_url: t.file_url,
                    binary_name: t.file_name,
                    binary_type: t.file_type
                })
            }
        }));

        setBulkProcessing(true);
        try {
            await axios.post(`/assets/${assetId}/targets/bulk`, { targets: formattedTargets });
            toast.success(`Successfully created ${formattedTargets.length} targets!`);
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("Multiple target creation error:", error);
            toast.error(error.response?.data?.message || "Failed to create targets.");
        } finally {
            setBulkProcessing(false);
        }
    };

    const handleBulkSubmit = async () => {
        if (!globalSettings.ownership_confirmed || !globalSettings.environment_confirmed) {
            toast.error("Please confirm scope authorization and operational safety.");
            return;
        }

        const validRows = importData.filter((_, idx) =>
            importErrors[idx]?.errors.length === 0
        ).map(row => {
            const { exposure, environment, target_type, ...rest } = row;

            // Normalize values for backend (exact casing required by API)
            const validEnvironments = ['Production', 'Staging', 'UAT', 'Development'];
            const normalizedEnv = validEnvironments.find(e => e.toLowerCase() === (environment || "").trim().toLowerCase()) || environment;

            const normalizedExposure = ['Internal', 'External'].find(e => e.toLowerCase() === (exposure || "").trim().toLowerCase()) || 'Internal';

            const allowedTypeNames = filteredTemplates.map(t => t.name);
            const normalizedType = allowedTypeNames.find(t => t.toLowerCase() === (target_type || "").trim().toLowerCase()) || target_type;

            return {
                ...rest,
                target_type: normalizedType,
                environment: normalizedEnv,
                metadata: {
                    internetExposure: normalizedExposure,
                },
                ownership_confirmed: globalSettings.ownership_confirmed,
                environment_confirmed: globalSettings.environment_confirmed
            };
        });

        if (validRows.length === 0) {
            toast.error('No valid targets to import');
            return;
        }

        setBulkProcessing(true);
        setImportProgress(0);

        try {
            // Use the new bulk endpoint
            await axios.post(`/assets/${assetId}/targets/bulk`, { targets: validRows });

            setImportProgress(100);
            toast.success(`Successfully imported ${validRows.length} targets!`);
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error('Bulk import error:', error);
            toast.error(error.response?.data?.message || 'Failed to import targets');
        } finally {
            setBulkProcessing(false);
        }
    };

    const addMultipleTargetRow = () => {
        const defaultTemplate = filteredTemplates[0] || TARGET_TEMPLATES[0];
        setMultipleTargets([...multipleTargets, {
            id: Date.now(),
            target_type: defaultTemplate.name,
            template_id: defaultTemplate.id,
            target_name: "",
            target_value: "",
            environment: multipleTargets[multipleTargets.length - 1]?.environment || globalSettings.environment,
            internetExposure: multipleTargets[multipleTargets.length - 1]?.internetExposure || globalSettings.internetExposure,
            ownership_confirmed: multipleTargets[multipleTargets.length - 1]?.ownership_confirmed || false,
            environment_confirmed: multipleTargets[multipleTargets.length - 1]?.environment_confirmed || false,
            notes: "",
        }]);
    };

    const removeMultipleTargetRow = (id: number) => {
        if (multipleTargets.length <= 1) return;
        setMultipleTargets(multipleTargets.filter(t => t.id !== id));
    };

    const updateMultipleTargetRow = (id: number, field: string, value: any) => {
        setMultipleTargets(multipleTargets.map(t => {
            if (t.id === id) {
                if (field === 'clear_file') {
                    return { ...t, file_url: null, file_name: null, file_type: null };
                }

                const updates: any = { [field]: value };
                if (field === 'template_id') {
                    const template = TARGET_TEMPLATES.find(temp => temp.id === value);
                    if (template) {
                        updates.target_type = template.name;
                    }
                }
                return { ...t, ...updates };
            }
            return t;
        }));
    };

    const handleMultipleFileUpload = async (id: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 250 * 1024 * 1024) {
            toast.error("File too large (max 250MB)");
            return;
        }

        // Set uploading state for this row
        setMultipleTargets(prev => prev.map(t => t.id === id ? { ...t, uploading: true } : t));

        const uploadFormData = new FormData();
        uploadFormData.append("file", file);

        try {
            const response = await axios.post("/assets/upload", uploadFormData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            setMultipleTargets(prev => prev.map(t => t.id === id ? {
                ...t,
                uploading: false,
                file_url: response.data.url,
                file_name: file.name,
                file_type: file.type
            } : t));

            toast.success("File uploaded successfully");
        } catch (err) {
            console.error("Upload error:", err);
            toast.error("Failed to upload file");
            setMultipleTargets(prev => prev.map(t => t.id === id ? { ...t, uploading: false } : t));
        }
    };

    const stepVariants = {
        enter: { x: 20, opacity: 0 },
        center: { x: 0, opacity: 1 },
        exit: { x: -20, opacity: 0 }
    };

    const STEPS = [
        { label: "Info", step: 0 },
        { label: "Mode", step: 1 },
        { label: importMode === 'bulk' ? "Import" : "Targets", step: 2 },
        { label: "Confirm", step: 3 }
    ];

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-end p-4 sm:p-6 bg-[#0B0F19]/60 backdrop-blur-sm">
            <motion.div
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 50, opacity: 0 }}
                className={`bg-white rounded-2xl w-full ${importMode === 'table' && step === 2 ? 'max-w-[95vw]' : 'max-w-xl'} shadow-2xl overflow-hidden flex flex-col max-h-[90vh] mt-4 border border-slate-200 transition-all duration-500`}
            >
                {/* Header Section */}
                <div className="bg-white px-6 py-4 relative flex justify-between items-center">
                    <div>
                        <div className="mb-0.5 flex items-center gap-2 text-[10px] font-medium text-slate-500">
                            <span>Asset Context</span>
                            <FiArrowRight className="w-2.5 h-2.5 text-slate-400" />
                            <span className="text-slate-900 font-semibold">{assetName}</span>
                        </div>
                        <h2 className="text-xl font-bold text-[#0A192F]">Add Target</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 hover:bg-slate-50 rounded-lg"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Dark Navy Progress Bar Area */}
                <div className="bg-[#0A192F] px-6 py-6 relative">
                    <div className="flex items-center justify-between relative max-w-md mx-auto">
                        {/* Connecting Line */}
                        <div className="absolute top-3.5 left-8 right-8 h-[1px] bg-slate-700 z-0">
                            <div
                                className="h-full bg-[#65D01E] transition-all duration-500 origin-left"
                                style={{ transform: `scaleX(${step / (STEPS.length - 1)})` }}
                            />
                        </div>

                        {STEPS.map((s, idx) => {
                            const isActive = step === s.step;
                            const isCompleted = step > s.step;
                            return (
                                <div key={s.label} className="flex flex-col items-center gap-2 relative z-10 w-20">
                                    <div
                                        className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs transition-all duration-300 ${isActive || isCompleted
                                            ? "bg-[#65D01E] text-white"
                                            : "bg-white text-[#0A192F]"
                                            }`}
                                    >
                                        {s.step + 1}
                                    </div>
                                    <span className="text-[10px] font-medium text-white/90">
                                        {s.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-6 bg-white">
                    <AnimatePresence mode="wait">
                        {step === 0 && (
                            <motion.div
                                key="step0"
                                variants={stepVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                className="space-y-5"
                            >
                                <div className="bg-[#EFF6FF] rounded-xl p-5 space-y-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 bg-[#BFDBFE] rounded-lg flex items-center justify-center text-[#1E40AF] shrink-0">
                                            <FiInfo className="w-4 h-4" />
                                        </div>
                                        <h3 className="text-base font-bold text-[#1E3A8A] leading-tight pt-0.5">
                                            What is a Target?
                                        </h3>
                                    </div>
                                    <div className="space-y-3">
                                        <p className="text-[#1E40AF] text-sm leading-relaxed">
                                            A <span className="font-bold">Target</span> is a specific entry point that is <span className="font-bold text-[#1E3A8A]">authorized for security testing</span> (URL, API base URI, mobile app ID, IP, or cloud resource).
                                        </p>
                                        <div className="bg-[#DBEAFE]/50 px-4 py-2.5 rounded-lg">
                                            <p className="text-[#1E40AF] font-bold text-xs">
                                                Important: If it is not added as a Target, it is out of scope.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-[#F8FAFC] rounded-xl p-5 border border-slate-100">
                                    <h4 className="text-sm font-bold text-[#0A192F] mb-4">Asset Identity Recap</h4>
                                    <div className="flex justify-between items-start">
                                        <div className="space-y-1.5">
                                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Category</span>
                                            <p className="text-sm font-bold text-[#0A192F]">{assetCategory}</p>
                                        </div>
                                        <div className="space-y-1.5 text-right">
                                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Primary Type</span>
                                            <p className="text-sm font-bold text-[#0A192F]">
                                                {Array.isArray(assetType) ? assetType.join(", ") : assetType}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {step === 1 && (
                            <motion.div
                                key="step1"
                                variants={stepVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                className="space-y-5"
                            >
                                <div className="text-center mb-2">
                                    <h3 className="text-base font-bold text-[#0A192F]">Select Import Mode</h3>
                                    <p className="text-xs text-slate-500 mt-1">How would you like to add targets?</p>
                                </div>
                                <div className="grid grid-cols-2 gap-6">
                                    <button
                                        onClick={() => handleModeSelect('table')}
                                        className="p-8 bg-white border-2 border-slate-200 rounded-2xl text-left hover:border-[#65D01E] hover:shadow-xl transition-all group flex flex-col items-center text-center"
                                    >
                                        <div className="w-20 h-20 rounded-2xl bg-[#EFF6FF] text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform mb-6">
                                            <FiPlus className="w-10 h-10" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-xl text-[#0A192F] mb-2">Add Targets</h4>
                                            <p className="text-sm text-slate-500 leading-relaxed">Manually add multiple targets in a comprehensive table view. Best for small to medium sets.</p>
                                        </div>
                                    </button>
                                    <button
                                        onClick={() => handleModeSelect('bulk')}
                                        className="p-8 bg-white border-2 border-slate-200 rounded-2xl text-left hover:border-[#65D01E] hover:shadow-xl transition-all group flex flex-col items-center text-center"
                                    >
                                        <div className="w-20 h-20 rounded-2xl bg-[#F0FDF4] text-green-600 flex items-center justify-center group-hover:scale-110 transition-transform mb-6">
                                            <FiUpload className="w-10 h-10" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-xl text-[#0A192F] mb-2">Bulk Import</h4>
                                            <p className="text-sm text-slate-500 leading-relaxed">Upload CSV or JSON files to import large volumes of targets instantly.</p>
                                        </div>
                                    </button>
                                </div>
                            </motion.div>
                        )}
                        {step === 2 && importMode === 'bulk' && (
                            <motion.div
                                key="step2-bulk"
                                variants={stepVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                className="space-y-4 max-h-[520px] overflow-y-auto pr-2"
                            >
                                <div className="text-center mb-4">
                                    <h3 className="text-base font-bold text-[#0A192F]">Bulk Target Import</h3>
                                    <p className="text-xs text-slate-500 mt-1">Upload CSV or JSON file with multiple targets</p>
                                </div>

                                {/* Format Selection */}
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <button
                                        onClick={() => setImportFormat('csv')}
                                        className={`py-2.5 px-4 rounded-xl font-bold text-xs transition-all border-2 flex items-center justify-center gap-2 ${importFormat === 'csv'
                                            ? 'bg-blue-50 border-blue-500 text-blue-700'
                                            : 'bg-white border-slate-100 text-slate-500 hover:border-slate-200'
                                            }`}
                                    >
                                        <FiFileText className="w-4 h-4" /> CSV Format
                                    </button>
                                    <button
                                        onClick={() => setImportFormat('json')}
                                        className={`py-2.5 px-4 rounded-xl font-bold text-xs transition-all border-2 flex items-center justify-center gap-2 ${importFormat === 'json'
                                            ? 'bg-blue-50 border-blue-500 text-blue-700'
                                            : 'bg-white border-slate-100 text-slate-500 hover:border-slate-200'
                                            }`}
                                    >
                                        <FiFile className="w-4 h-4" /> JSON Format
                                    </button>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    {/* Download Template */}
                                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col justify-between">
                                        <div>
                                            <h4 className="font-bold text-xs text-slate-900 mb-1">Step 1: Get Template</h4>
                                            <p className="text-[10px] text-slate-500 mb-3">Download our sample {importFormat.toUpperCase()} file to ensure data is correctly formatted.</p>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <button
                                                onClick={importFormat === 'csv' ? generateCSVTemplate : generateJSONTemplate}
                                                className="w-full bg-slate-800 text-white py-2 rounded-lg text-[10px] font-bold hover:bg-black transition-colors flex items-center justify-center gap-2"
                                            >
                                                <FiDownload className="w-3.5 h-3.5" /> Download Template
                                            </button>
                                            <button
                                                onClick={() => setShowImportGuide(true)}
                                                className="w-full bg-white border border-slate-200 text-slate-600 py-2 rounded-lg text-[10px] font-bold hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <FiFileText className="w-3.5 h-3.5" /> View Import Guide
                                            </button>
                                        </div>
                                    </div>

                                    {/* File Upload Area */}
                                    <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:border-blue-400 transition-colors relative group">
                                        <div className="py-2">
                                            <FiUpload className="w-6 h-6 text-slate-300 mx-auto mb-2 group-hover:text-blue-500 transition-colors" />
                                            <h4 className="font-bold text-xs text-slate-900 mb-1">Step 2: Upload File</h4>
                                            <p className="text-[10px] text-slate-500">Drag & drop or click</p>
                                        </div>
                                        <input
                                            type="file"
                                            accept={importFormat === 'csv' ? '.csv' : '.json'}
                                            onChange={handleBulkFileUpload}
                                            className="absolute inset-0 opacity-0 cursor-pointer"
                                        />
                                    </div>
                                </div>

                                {/* Import Preview */}
                                {importData.length > 0 && (
                                    <div className="space-y-3 mt-4">
                                        <div className="flex items-center justify-between px-1">
                                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                                <FiZap className="w-3 h-3" /> Preview ({importData.length})
                                            </h4>
                                            {importErrors.some(e => e.errors.length > 0) && (
                                                <div className="flex items-center gap-1 text-[10px] font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded border border-red-100">
                                                    <FiAlertTriangle className="w-3 h-3" /> Fix {importErrors.filter(e => e.errors.length > 0).length} errors
                                                </div>
                                            )}
                                        </div>
                                        <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                                            <div className="max-h-40 overflow-y-auto">
                                                <table className="w-full text-left text-[10px]">
                                                    <thead className="bg-slate-50 sticky top-0 border-b border-slate-100">
                                                        <tr>
                                                            <th className="px-3 py-2 font-bold text-slate-600">Target Name</th>
                                                            <th className="px-3 py-2 font-bold text-slate-600">Type</th>
                                                            <th className="px-3 py-2 font-bold text-slate-600">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50">
                                                        {importData.map((row, idx) => {
                                                            const rowErrors = importErrors[idx]?.errors || [];
                                                            return (
                                                                <tr key={idx} className={rowErrors.length > 0 ? "bg-red-50/50" : ""}>
                                                                    <td className="px-3 py-2 font-medium text-slate-700">{row.target_name}</td>
                                                                    <td className="px-3 py-2 text-slate-500">{row.target_type}</td>
                                                                    <td className="px-3 py-2">
                                                                        {rowErrors.length === 0 ? (
                                                                            <span className="text-green-600 flex items-center gap-1"><FiCheck /> Ready</span>
                                                                        ) : (
                                                                            <span className="text-red-500 font-bold truncate block max-w-[120px]" title={rowErrors.join(', ')}>
                                                                                {rowErrors[0]}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}


                        {step === 2 && importMode === 'table' && (
                            <motion.div
                                key="step2-table"
                                variants={stepVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                className="space-y-4 flex flex-col h-[520px]"
                            >
                                {/* Header & Actions */}
                                <div className="flex items-end justify-between shrink-0 mb-2">
                                    <div>
                                        <h3 className="text-base font-bold text-[#0A192F]">Add Targets</h3>
                                        <p className="text-xs text-slate-500 mt-1">Fill in the details for each target you want to add.</p>
                                    </div>
                                    <button
                                        onClick={addMultipleTargetRow}
                                        className="py-2 px-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 rounded-lg text-xs font-bold text-slate-700 flex items-center gap-2 transition-all shadow-sm"
                                    >
                                        <FiPlus className="w-3.5 h-3.5" />
                                        Add Row
                                    </button>
                                </div>

                                {/* Table Area */}
                                <div className="flex-1 overflow-hidden bg-white border border-slate-200 rounded-xl flex flex-col shadow-sm">
                                    <div className="overflow-auto flex-1">
                                        <table className="w-full text-xs">
                                            <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm border-b border-slate-200">
                                                <tr>
                                                    <th className="px-3 py-3 text-left font-bold text-slate-500 w-[180px]">Type</th>
                                                    <th className="px-3 py-3 text-left font-bold text-slate-500 w-[200px]">Name</th>
                                                    <th className="px-3 py-3 text-left font-bold text-slate-500 min-w-[250px]">Value (URL/IP)</th>
                                                    <th className="px-3 py-3 text-left font-bold text-slate-500 w-[140px]">Environment</th>
                                                    <th className="px-3 py-3 text-left font-bold text-slate-500 w-[140px]">Exposure</th>
                                                    <th className="px-3 py-3 text-left font-bold text-slate-500 w-[200px]">Notes</th>
                                                    <th className="px-2 py-3 w-[50px]"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-50">
                                                {multipleTargets.map((row, idx) => {
                                                    const isMobile = ['android-app', 'ios-app'].includes(row.template_id);
                                                    return (
                                                        <tr key={row.id} className="group hover:bg-slate-50 transition-colors">
                                                            <td className="p-2 align-middle">
                                                                <select
                                                                    value={row.template_id}
                                                                    onChange={(e) => updateMultipleTargetRow(row.id, 'template_id', e.target.value)}
                                                                    className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-[#82D616] outline-none shadow-sm font-medium"
                                                                >
                                                                    {filteredTemplates.map(t => (
                                                                        <option key={t.id} value={t.id}>{t.name}</option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td className="p-2 align-middle">
                                                                <input
                                                                    type="text"
                                                                    value={row.target_name}
                                                                    onChange={(e) => updateMultipleTargetRow(row.id, 'target_name', e.target.value)}
                                                                    placeholder="e.g. Prod API"
                                                                    className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-[#82D616] outline-none font-bold text-slate-700 shadow-sm"
                                                                />
                                                            </td>
                                                            <td className="p-2 align-middle">
                                                                <div className="space-y-2">
                                                                    <input
                                                                        type="text"
                                                                        value={row.target_value}
                                                                        onChange={(e) => updateMultipleTargetRow(row.id, 'target_value', e.target.value)}
                                                                        placeholder={getFieldConfig(row.template_id).placeholder}
                                                                        className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-[#82D616] outline-none font-mono shadow-sm"
                                                                    />
                                                                    {isMobile && (
                                                                        <div className="relative">
                                                                            {row.uploading ? (
                                                                                <div className="flex items-center gap-2 text-[10px] text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
                                                                                    <FiRefreshCw className="animate-spin" /> Uploading...
                                                                                </div>
                                                                            ) : row.file_url ? (
                                                                                <div className="flex items-center justify-between gap-1 text-[10px] bg-green-50 text-green-700 px-2 py-1 rounded-md border border-green-100">
                                                                                    <span className="truncate max-w-[120px]">{row.file_name}</span>
                                                                                    <button onClick={() => updateMultipleTargetRow(row.id, 'clear_file', null)} className="text-red-400"><FiX /></button>
                                                                                </div>
                                                                            ) : (
                                                                                <input
                                                                                    type="file"
                                                                                    accept={row.template_id === 'android-app' ? '.apk,.aab' : '.ipa'}
                                                                                    onChange={(e) => handleMultipleFileUpload(row.id, e)}
                                                                                    className="text-[10px] w-full file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-100"
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="p-2 align-middle">
                                                                <select
                                                                    value={row.environment}
                                                                    onChange={(e) => updateMultipleTargetRow(row.id, 'environment', e.target.value)}
                                                                    className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-[#82D616] outline-none shadow-sm"
                                                                >
                                                                    <option>Production</option>
                                                                    <option>Staging</option>
                                                                    <option>UAT</option>
                                                                    <option>Development</option>
                                                                </select>
                                                            </td>
                                                            <td className="p-2 align-middle">
                                                                <select
                                                                    value={row.internetExposure}
                                                                    onChange={(e) => updateMultipleTargetRow(row.id, 'internetExposure', e.target.value)}
                                                                    className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-[#82D616] outline-none shadow-sm"
                                                                >
                                                                    <option value="Internal">Internal</option>
                                                                    <option value="External">External</option>
                                                                </select>
                                                            </td>
                                                            <td className="p-2 align-middle">
                                                                <textarea
                                                                    value={row.notes}
                                                                    onChange={(e) => updateMultipleTargetRow(row.id, 'notes', e.target.value)}
                                                                    placeholder="Additional notes..."
                                                                    rows={1}
                                                                    className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-[#82D616] outline-none shadow-sm resize-none"
                                                                />
                                                            </td>
                                                            <td className="p-2 align-middle text-center">
                                                                <div className="flex items-center justify-center gap-1 mt-1">
                                                                    <button
                                                                        onClick={() => {
                                                                            const newRow = { ...row, id: Date.now() };
                                                                            setMultipleTargets([...multipleTargets.slice(0, idx + 1), newRow, ...multipleTargets.slice(idx + 1)]);
                                                                        }}
                                                                        title="Duplicate Row"
                                                                        className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-md transition-colors"
                                                                    >
                                                                        <FiPlus className="w-3.5 h-3.5" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => removeMultipleTargetRow(row.id)}
                                                                        disabled={multipleTargets.length <= 1}
                                                                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 rounded-md transition-colors"
                                                                    >
                                                                        <FiTrash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    {/* Empty state or helpful tip if needed */}
                                    {multipleTargets.length === 0 && (
                                        <div className="p-8 text-center text-slate-400 text-xs">
                                            No targets added. Click "Add Row" to begin.
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}

                        {step === 3 && (
                            <motion.div
                                key="step3"
                                variants={stepVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                className="space-y-8"
                            >
                                <div className="text-center mb-2">
                                    <h3 className="text-base font-bold text-[#0A192F]">Final Confirmations</h3>
                                    <p className="text-xs text-slate-500 mt-1">Please authorize the scope and confirm operational safety.</p>
                                </div>

                                <div className="space-y-6">
                                    {/* Scope Authorization */}
                                    <div 
                                        onClick={() => setGlobalSettings(prev => ({ ...prev, ownership_confirmed: !prev.ownership_confirmed }))}
                                        className={clsx(
                                            "p-6 rounded-2xl border-2 transition-all cursor-pointer group",
                                            globalSettings.ownership_confirmed ? "bg-emerald-50 border-emerald-500" : "bg-white border-slate-100 hover:border-slate-200"
                                        )}
                                    >
                                        <div className="flex gap-4">
                                            <div className={clsx(
                                                "w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors",
                                                globalSettings.ownership_confirmed ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300 group-hover:border-slate-400"
                                            )}>
                                                {globalSettings.ownership_confirmed && <FiCheck className="w-4 h-4" />}
                                            </div>
                                            <div className="space-y-2">
                                                <h4 className="font-bold text-sm text-[#0A192F]">Scope Authorization</h4>
                                                <p className="text-xs text-slate-500 leading-relaxed">
                                                    I confirm that all selected targets are within my organization's legal authorization for security testing and that I have the right to authorize this engagement.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Operational Safety */}
                                    <div 
                                        onClick={() => setGlobalSettings(prev => ({ ...prev, environment_confirmed: !prev.environment_confirmed }))}
                                        className={clsx(
                                            "p-6 rounded-2xl border-2 transition-all cursor-pointer group",
                                            globalSettings.environment_confirmed ? "bg-emerald-50 border-emerald-500" : "bg-white border-slate-100 hover:border-slate-200"
                                        )}
                                    >
                                        <div className="flex gap-4">
                                            <div className={clsx(
                                                "w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors",
                                                globalSettings.environment_confirmed ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300 group-hover:border-slate-400"
                                            )}>
                                                {globalSettings.environment_confirmed && <FiCheck className="w-4 h-4" />}
                                            </div>
                                            <div className="space-y-2">
                                                <h4 className="font-bold text-sm text-[#0A192F]">Operational Safety Confirmation</h4>
                                                <p className="text-xs text-slate-500 leading-relaxed">
                                                    I confirm that these targets are safe for testing and that researchers are aware of the operational boundaries to prevent service disruption.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Footer */}
                <div className="p-6 bg-[#FAFAFA] mt-auto flex justify-end shrink-0">
                    <div className="flex justify-between w-full items-center">
                        <button
                            onClick={step === 0 ? onClose : prevStep}
                            className="px-6 py-3 text-slate-500 font-bold text-sm flex items-center gap-2 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            {step === 0 ? <FiX className="w-4 h-4" /> : <FiArrowLeft className="w-4 h-4" />}
                            {step === 0 ? "Cancel" : "Back"}
                        </button>

                        {/* Validation & Next Buttons */}
                        {step === 0 && (
                            <button
                                onClick={nextStep}
                                disabled={uploading}
                                className={`px-8 py-3 rounded-lg font-bold text-sm flex items-center gap-2 transition-all shadow-lg ${uploading
                                    ? "bg-slate-200 text-slate-400 shadow-none cursor-not-allowed"
                                    : "bg-[#65D01E] text-white hover:bg-[#5ac118] shadow-[#65D01E]/30"
                                    }`}
                            >
                                {uploading ? "Uploading..." : "Continue"} <FiArrowRight className="w-4 h-4" />
                            </button>
                        )}

                        {/* Bulk Mode - Continue to Confirm (Step 2) */}
                        {step === 2 && importMode === 'bulk' && (
                            <button
                                onClick={nextStep}
                                disabled={importData.length === 0 || importErrors.some(e => e.errors.length > 0)}
                                className={`px-8 py-3 rounded-lg font-bold text-sm flex items-center gap-2 transition-all shadow-lg ${(importData.length === 0 || importErrors.some(e => e.errors.length > 0))
                                    ? "bg-slate-200 text-slate-400 shadow-none cursor-not-allowed"
                                    : "bg-[#65D01E] text-white hover:bg-[#5ac118] shadow-[#65D01E]/30"
                                    }`}
                            >
                                Continue <FiArrowRight className="w-4 h-4" />
                            </button>
                        )}

                        {/* Table Mode - Continue to Confirm (Step 2) */}
                        {step === 2 && importMode === 'table' && (
                            <button
                                onClick={nextStep}
                                disabled={multipleTargets.some(t => t.uploading) || multipleTargets.filter(t => t.target_name && (t.target_value || t.file_url)).length === 0}
                                className={`px-8 py-3 rounded-lg font-bold text-sm flex items-center gap-2 transition-all shadow-lg ${(multipleTargets.some(t => t.uploading) || multipleTargets.filter(t => t.target_name && (t.target_value || t.file_url)).length === 0)
                                    ? "bg-slate-200 text-slate-400 shadow-none cursor-not-allowed"
                                    : "bg-[#65D01E] text-white hover:bg-[#5ac118] shadow-[#65D01E]/30"
                                    }`}
                            >
                                {multipleTargets.some(t => t.uploading) ? "Uploading Files..." : "Continue"} <FiArrowRight className="w-4 h-4" />
                            </button>
                        )}

                        {/* Final Step - Submit (Step 3) */}
                        {step === 3 && (
                            <button
                                onClick={importMode === 'bulk' ? handleBulkSubmit : handleMultipleSubmit}
                                disabled={bulkProcessing || !globalSettings.ownership_confirmed || !globalSettings.environment_confirmed}
                                className={`px-8 py-3 rounded-lg font-bold text-sm flex items-center gap-2 transition-all shadow-lg ${(bulkProcessing || !globalSettings.ownership_confirmed || !globalSettings.environment_confirmed)
                                    ? "bg-slate-200 text-slate-400 shadow-none cursor-not-allowed"
                                    : "bg-[#65D01E] text-white hover:bg-[#5ac118] shadow-[#65D01E]/30"
                                    }`}
                            >
                                {bulkProcessing ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        {importMode === 'bulk' ? 'Importing...' : 'Creating...'}
                                    </>
                                ) : (
                                    <>
                                        {importMode === 'bulk' ? `Import ${importData.filter((_, idx) => importErrors[idx]?.errors.length === 0).length} Targets` : 'Launch Targets'}
                                        <FiZap className="w-4 h-4" />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* ==================== IMPORT GUIDE MODAL ==================== */}
            <AnimatePresence>
                {showImportGuide && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
                        onClick={() => setShowImportGuide(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-200">
                                        <FiFileText className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-black text-slate-900 tracking-tight">Bulk Import Guide</h2>
                                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">100% Comprehensive Mapping</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowImportGuide(false)}
                                    className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400 hover:text-slate-900"
                                >
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
                                {/* Case Sensitivity Alert */}
                                <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl flex gap-3">
                                    <FiCheckCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                                    <div className="text-sm">
                                        <p className="font-bold text-blue-900 mb-1">Advanced: Case-Insensitive Support</p>
                                        <p className="text-blue-800 leading-relaxed">The system now automatically normalizes values. For example, <code className="bg-blue-100 px-1 rounded">internal</code> will be treated as <span className="font-bold">Internal</span>. Standard capitalization is still recommended.</p>
                                    </div>
                                </div>

                                {/* Mapping Tables */}
                                <div className="space-y-6">
                                    {/* Table Style Wrapper */}
                                    <style dangerouslySetInnerHTML={{ __html: `
                                        .guide-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12px; }
                                        .guide-table th { background: #f8fafc; padding: 12px 16px; text-align: left; font-weight: 800; color: #64748b; border-bottom: 2px solid #e2e8f0; text-transform: uppercase; letter-spacing: 0.05em; }
                                        .guide-table td { padding: 12px 16px; border-bottom: 1px solid #f1f5f9; color: #1e293b; font-weight: 500; }
                                        .guide-table tr:hover td { background: #f8fafc; }
                                        .asset-cat-header { background: #1e293b; color: white; padding: 10px 16px; font-size: 11px; font-weight: 900; border-radius: 8px 8px 0 0; display: inline-block; margin-bottom: -2px; }
                                        .badge-tag { background: #eff6ff; color: #2563eb; padding: 2px 8px; rounded: 4px; font-weight: 800; font-size: 10px; border: 1px solid #dbeafe; }
                                    `}} />

                                    {/* Digital Apps */}
                                    <div>
                                        <div className="asset-cat-header tracking-widest uppercase">1. `target_type` for Digital Applications</div>
                                        <div className="border border-slate-200 rounded-xl rounded-tl-none overflow-hidden hover:border-slate-300 transition-colors">
                                            <table className="guide-table">
                                                <thead>
                                                    <tr>
                                                        <th>Asset Primary Type</th>
                                                        <th>Valid `target_type` (String in File)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr><td>Web Application</td><td><code className="bg-slate-100 px-1 rounded">Web URL Target</code>, <code className="bg-slate-100 px-1 rounded">Admin/Restricted Web URL</code>, <code className="bg-slate-100 px-1 rounded">API Base URI Target</code>, <code className="bg-slate-100 px-1 rounded">Custom Target</code></td></tr>
                                                    <tr><td>API Service</td><td><code className="bg-slate-100 px-1 rounded">API Base URI Target</code>, <code className="bg-slate-100 px-1 rounded">Webhook/Callback URL</code>, <code className="bg-slate-100 px-1 rounded">Custom Target</code></td></tr>
                                                    <tr><td>Mobile Application</td><td><code className="bg-slate-100 px-1 rounded">Android App</code>, <code className="bg-slate-100 px-1 rounded">iOS App</code>, <code className="bg-slate-100 px-1 rounded">Mobile App Target</code>, <code className="bg-slate-100 px-1 rounded">API Base URI Target</code>, <code className="bg-slate-100 px-1 rounded">Custom Target</code></td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Infrastructure */}
                                    <div>
                                        <div className="asset-cat-header tracking-widest uppercase bg-slate-700">2. `target_type` for Infrastructure</div>
                                        <div className="border border-slate-200 rounded-xl rounded-tl-none overflow-hidden hover:border-slate-300 transition-colors">
                                            <table className="guide-table">
                                                <thead>
                                                    <tr>
                                                        <th>Asset Primary Type</th>
                                                        <th>Valid `target_type` (String in File)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr><td>External infrastructure</td><td>Web URL Target, External IP / Host Target, CIDR / Network Segment Target, Custom Target</td></tr>
                                                    <tr><td>Internal infrastructure</td><td>Web URL Target, External IP / Host Target, CIDR / Network Segment Target, Custom Target</td></tr>
                                                    <tr><td>External Facing web applications</td><td>Web URL Target, Admin/Restricted Web URL, Custom Target</td></tr>
                                                    <tr><td>Server / Host</td><td>External IP / Host Target, Custom Target</td></tr>
                                                    <tr><td>Network Segment</td><td>CIDR / Network Segment Target, Custom Target</td></tr>
                                                    <tr><td>Cloud instance</td><td>Cloud Account/Project Target, Custom Target</td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Row 3 & 4 */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <div className="asset-cat-header tracking-widest uppercase bg-blue-900">3. Identity & Data</div>
                                            <div className="border border-slate-200 rounded-xl rounded-tl-none overflow-hidden">
                                                <table className="guide-table">
                                                    <tbody>
                                                        <tr><td>Identity Provider</td><td>Web URL Target, Custom Target</td></tr>
                                                        <tr><td>Other Types</td><td>Custom Target</td></tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="asset-cat-header tracking-widest uppercase bg-purple-900">4. Code & Supply Chain</div>
                                            <div className="border border-slate-200 rounded-xl rounded-tl-none overflow-hidden">
                                                <table className="guide-table">
                                                    <tbody>
                                                        <tr><td>Repo / Pipeline</td><td>Web URL Target, Custom Target</td></tr>
                                                        <tr><td>Other Types</td><td>Custom Target</td></tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                                <button
                                    onClick={() => setShowImportGuide(false)}
                                    className="px-6 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-black transition-colors"
                                >
                                    Got it, close
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CreateTargetWizard;
